/**
 * El selector de proveedor: escribible, filtrable por nombre y con alta rápida.
 *
 * Reemplaza al `<select>` nativo en las dos pantallas que lo usan —el producto
 * y la compra— porque hacía falta lo que un `<select>` no da: buscar
 * escribiendo, y crear un proveedor sin salir del formulario.
 *
 * ── Por qué a mano y no una librería ────────────────────────────────────────
 *
 * Un combobox accesible son cuatro cosas: `role="combobox"` con su `listbox`,
 * flechas y Enter, `aria-activedescendant`, y Escape que cierra sin cambiar
 * nada. Eso cabe acá. Traer una dependencia nueva para esto agregaría más
 * superficie que la que resuelve, y la regla del proyecto es explícita: sin
 * dependencias nuevas.
 *
 * ── El alta rápida NO escribe en la base ────────────────────────────────────
 *
 * Regla del dueño, literal: «si el usuario le da cancelar, solo se sale, y
 * aunque haya intentado poner el nombre, no lo guardó». Así que «agregar
 * proveedor» **no llama a la API**: guarda el nombre en el estado del
 * formulario (`pendingName`) y quien guarda el producto o la compra lo crea
 * justo antes, con `resolveSupplierId`. Si se cancela, no se llamó a nadie y
 * no hay nada que deshacer.
 *
 * La alternativa —crear de una y borrar al cancelar— depende de que el
 * navegador siga vivo: cerrar la pestaña dejaría un proveedor huérfano en la
 * base para siempre.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useApi } from '@/lib/hooks';
import { Button, Field, Modal, Note } from '../ui';

export interface Supplier {
  id: string;
  name: string;
  identification: string | null;
  email: string | null;
  phone: string | null;
  /** Aparte de `phone`: no siempre es el mismo número (0030). */
  whatsapp: string | null;
  address: string | null;
  notes: string | null;
  is_active: number;
  /** Cuántas compras cuelgan de él: lo que decide si se puede apagar sin dejar huecos. */
  purchase_count: number;
  /** Cuántos productos lo tienen como proveedor preferido. */
  item_count: number;
}

/** Los proveedores de la organización. Una sola consulta, compartida por las tres pantallas. */
export function useSuppliers() {
  return useApi<{ suppliers: Supplier[] }>('/admin/suppliers');
}

/**
 * Lo elegido en el campo: o un proveedor que ya existe, o un nombre que
 * todavía no existe en ningún lado.
 *
 * Nunca los dos a la vez. Se modela con dos campos y no con un `string` con
 * prefijo (`new:Acme`) porque un id y un nombre tecleado son cosas distintas y
 * confundirlos es cómo se termina mandando la palabra «new:Acme» a la base.
 */
export interface SupplierChoice {
  id: string | null;
  pendingName: string | null;
}

export const NO_SUPPLIER: SupplierChoice = { id: null, pendingName: null };

export function supplierChoiceOf(id: string | null | undefined): SupplierChoice {
  return { id: id ?? null, pendingName: null };
}

export function isSupplierChosen(choice: SupplierChoice): boolean {
  return Boolean(choice.id ?? choice.pendingName);
}

/**
 * Convierte lo elegido en un id real, creando el proveedor si hacía falta.
 *
 * Se llama **al guardar**, no antes. Devuelve también si lo creó, porque la
 * pantalla que lo llama ofrece completar la ficha después de guardar — y solo
 * tiene sentido ofrecerlo cuando el proveedor es nuevo.
 */
export async function resolveSupplierId(
  choice: SupplierChoice,
): Promise<{ id: string | null; created: boolean }> {
  if (choice.pendingName) {
    const { id } = await api.post<{ id: string }>('/admin/suppliers', { name: choice.pendingName });
    return { id, created: true };
  }
  return { id: choice.id, created: false };
}

/**
 * Sin tildes y en minúscula, para que «Quimica» encuentre a «Química».
 *
 * Se recorre por punto de código en vez de con una expresión regular: el
 * rango de marcas combinantes que `NFD` deja sueltas (U+0300 a U+036F) son
 * caracteres invisibles, y escritos dentro de un `[...]` en el fuente quedan
 * pegados al corchete y cualquier editor los puede comer sin que se note.
 */
function fold(text: string): string {
  let out = '';
  for (const ch of text.normalize('NFD')) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 0x300 && code <= 0x36f) continue;
    out += ch;
  }
  return out.toLowerCase();
}

export function SupplierPicker({
  value, onChange, suppliers, loading, placeholder = 'Buscá por nombre…', autoFocus,
}: {
  value: SupplierChoice;
  onChange: (next: SupplierChoice) => void;
  suppliers: Supplier[];
  loading?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const listId = useId();
  const optionId = (i: number) => `${listId}-opt-${i}`;

  const [open, setOpen] = useState(false);
  const [queryText, setQueryText] = useState('');
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(false);

  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = value.id ? suppliers.find((s) => s.id === value.id) ?? null : null;

  /* Solo se ofrecen los activos: apagar un proveedor es justamente decir «a
     este ya no le compramos». El que está elegido se muestra igual aunque esté
     apagado —puede ser una compra vieja que se está editando—, porque
     esconderlo haría ver el campo vacío y parecería un dato perdido. */
  const options = useMemo(() => {
    const needle = fold(queryText.trim());
    return suppliers
      .filter((s) => s.is_active === 1 || s.id === value.id)
      .filter((s) => !needle || fold(s.name).includes(needle));
  }, [suppliers, queryText, value.id]);

  // Cerrar al tocar afuera: el panel flota y no hay scrim que lo tape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // La opción activa siempre visible: con cinco a la vista y scroll, moverse
  // con las flechas hasta la número doce no puede dejar el foco fuera de la caja.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLLIElement>(`#${CSS.escape(optionId(active))}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  function choose(supplier: Supplier) {
    onChange({ id: supplier.id, pendingName: null });
    setQueryText('');
    setOpen(false);
    inputRef.current?.focus();
  }

  function clear() {
    onChange(NO_SUPPLIER);
    setQueryText('');
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(0); return; }
      if (options.length === 0) return;
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setActive((i) => (i + delta + options.length) % options.length);
      return;
    }
    if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      const pick = options[active];
      if (pick) choose(pick);
      return;
    }
    if (e.key === 'Escape') {
      // Escape cierra la lista sin cambiar lo elegido. Si ya estaba cerrada no
      // se lo come: puede haber un modal detrás esperando esa tecla.
      if (open) { e.preventDefault(); setOpen(false); setQueryText(''); }
      return;
    }
    if (e.key === 'Tab') setOpen(false);
  }

  const label = value.pendingName ?? selected?.name ?? '';

  return (
    <>
      <div className="row picker-row">
        <div className="picker" ref={boxRef}>
          {/* Con algo elegido, el campo muestra el nombre y no se teclea
              encima: para cambiarlo se quita primero. Es lo que evita el
              clásico «dice Acme pero en realidad no hay nada seleccionado». */}
          {label ? (
            <div className="picker__chosen">
              <span className="picker__chosen-name">{label}</span>
              {value.pendingName && (
                <span className="badge badge--info badge--plain">nuevo · se crea al guardar</span>
              )}
              <button
                type="button"
                className="picker__clear"
                onClick={clear}
                aria-label={`Quitar el proveedor ${label}`}
              >
                ✕
              </button>
            </div>
          ) : (
            <input
              ref={inputRef}
              className="input"
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={open && options[active] ? optionId(active) : undefined}
              placeholder={loading ? 'Cargando proveedores…' : placeholder}
              value={queryText}
              autoFocus={autoFocus}
              onChange={(e) => { setQueryText(e.target.value); setOpen(true); setActive(0); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
            />
          )}

          {open && !label && (
            <ul className="picker__list" id={listId} role="listbox" ref={listRef}>
              {options.length === 0 ? (
                <li className="picker__empty" role="presentation">
                  {suppliers.length === 0
                    ? 'Todavía no hay proveedores cargados.'
                    : `Ningún proveedor coincide con «${queryText.trim()}».`}
                </li>
              ) : (
                options.map((s, i) => (
                  <li
                    key={s.id}
                    id={optionId(i)}
                    role="option"
                    aria-selected={i === active}
                    className={`picker__opt${i === active ? ' is-active' : ''}`}
                    // `mousedown` y no `click`: el click llega después del blur
                    // del input, y para entonces la lista ya se cerró.
                    onMouseDown={(e) => { e.preventDefault(); choose(s); }}
                    onMouseEnter={() => setActive(i)}
                  >
                    <span className="picker__opt-name">{s.name}</span>
                    {s.is_active === 0 && <span className="badge badge--muted">inactivo</span>}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        {/* Fijo al lado del campo, no como última opción de la lista: así no se
            pierde al filtrar, no se selecciona sin querer con Enter, y sigue
            estando a la vista con cuarenta proveedores cargados. */}
        <Button onClick={() => setAdding(true)}>Agregar proveedor</Button>
      </div>

      {adding && (
        <QuickAddSupplier
          initialName={queryText.trim()}
          suppliers={suppliers}
          onClose={() => setAdding(false)}
          onPick={(supplier) => { choose(supplier); setAdding(false); }}
          onDefer={(name) => {
            onChange({ id: null, pendingName: name });
            setQueryText('');
            setOpen(false);
            setAdding(false);
          }}
        />
      )}
    </>
  );
}

/**
 * El alta rápida: **solo el nombre**.
 *
 * Nada más, aunque la tabla tenga cédula, correo, teléfono y dirección: quien
 * está registrando una compra no tiene esos datos a mano, y pedírselos
 * convierte una interrupción de tres segundos en un formulario. El resto se
 * completa después, cuando la pantalla lo ofrece.
 *
 * El choque de nombre se detecta acá y **no** se resuelve creando un duplicado
 * con otro nombre: se ofrece el que ya existe, que casi siempre es lo que la
 * persona quería. `unique (organization_id, name)` sigue siendo la garantía —
 * esto solo evita llegar hasta ella para enterarse.
 */
function QuickAddSupplier({ initialName, suppliers, onClose, onPick, onDefer }: {
  initialName: string;
  suppliers: Supplier[];
  onClose: () => void;
  onPick: (supplier: Supplier) => void;
  onDefer: (name: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const clean = name.trim();
  const existing = suppliers.find((s) => fold(s.name) === fold(clean));

  return (
    <Modal
      title="Agregar proveedor"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!clean || Boolean(existing)}
            onClick={() => onDefer(clean)}
          >
            Agregar
          </Button>
        </>
      }
    >
      <Note tone="info">
        Por ahora solo el nombre. El proveedor se crea cuando guardés, y ahí mismo vas a poder
        completarle la cédula, el correo y el teléfono.
      </Note>

      <div className="mt-2">
        <Field
          label="Nombre"
          required
          error={existing ? `Ya existe un proveedor llamado «${existing.name}».` : undefined}
        >
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && clean && !existing) { e.preventDefault(); onDefer(clean); }
            }}
          />
        </Field>

        {existing && (
          <Button onClick={() => onPick(existing)}>Usar «{existing.name}»</Button>
        )}
      </div>
    </Modal>
  );
}
